/**
 * @fileoverview CVA style variants for MobileBottomNav component
 * @description Headless-friendly style system using class-variance-authority
 * @module MobileBottomNav/styles
 * @version 1.0.0
 */

import { cva, type VariantProps } from "class-variance-authority"
import { NavVariant, NavSize, NavItemState, LabelPosition, IconSize } from "./types"

/* ============================================
   Root Container Variants
   ============================================ */

/**
 * Root container styles with variant support
 * @description Styles for the main navigation wrapper
 */
export const rootVariants = cva(
  [
    // Base positioning
    "fixed bottom-0 left-0 right-0",
    // Safe area padding (handled by CSS custom properties)
    "pb-[env(safe-area-inset-bottom,0px)]",
    "pl-[env(safe-area-inset-left,0px)]",
    "pr-[env(safe-area-inset-right,0px)]",
    // Hardware acceleration
    "transform-gpu",
    // Base transition
    "transition-transform duration-300 ease-out",
  ],
  {
    variants: {
      /**
       * Visual variant of the navigation
       */
      variant: {
        [NavVariant.GLASS]: [
          // Glassmorphism effect
          "bg-white/[0.72] dark:bg-black/[0.72]",
          "backdrop-blur-xl backdrop-saturate-[180%]",
          "border-t border-white/[0.18] dark:border-white/[0.08]",
          // Subtle shadow
          "shadow-[0_-1px_3px_rgba(0,0,0,0.05)]",
        ],
        [NavVariant.SOLID]: [
          // Solid background
          "bg-background",
          "border-t border-border",
          // More prominent shadow
          "shadow-[0_-2px_10px_rgba(0,0,0,0.1)]",
        ],
        [NavVariant.HEADLESS]: [
          // No default styles - fully customizable
          "",
        ],
      },
      /**
       * Size preset affecting height
       */
      size: {
        [NavSize.SMALL]: "",
        [NavSize.MEDIUM]: "",
        [NavSize.LARGE]: "",
      },
      /**
       * Visibility state
       */
      visible: {
        true: "translate-y-0",
        false: "translate-y-full",
      },
      /**
       * Full width with container and centering
       */
      full: {
        true: "mx-auto max-w-screen-sm",
        false: "",
      },
      /**
       * Border radius preset
       */
      rounded: {
        none: "rounded-none",
        sm: "rounded-t-lg",
        md: "rounded-t-2xl",
        lg: "rounded-t-3xl",
        xl: "rounded-t-[2rem]",
        full: "rounded-t-full",
      },
    },
    defaultVariants: {
      variant: NavVariant.GLASS,
      size: NavSize.MEDIUM,
      visible: true,
      full: false,
      rounded: "none",
    },
  },
)

/**
 * Extract variant props type for Root
 */
export type RootVariantProps = VariantProps<typeof rootVariants>

/* ============================================
   Navigation List Variants
   ============================================ */

/**
 * Navigation list styles
 * @description Styles for the ul element containing nav items
 */
export const navListVariants = cva(
  [
    // Flexbox layout
    "flex items-center",
    // Full width
    "w-full",
    // Reset list styles
    "list-none m-0 p-0",
  ],
  {
    variants: {
      /**
       * Size affecting padding and height
       */
      size: {
        [NavSize.SMALL]: "h-14 px-2 py-1",
        [NavSize.MEDIUM]: "h-[4.5rem] px-4 py-2",
        [NavSize.LARGE]: "h-22 px-6 py-3",
      },
      /**
       * Justify content alignment
       */
      justify: {
        start: "justify-start",
        center: "justify-center",
        end: "justify-end",
        around: "justify-around",
        between: "justify-between",
        evenly: "justify-evenly",
      },
    },
    defaultVariants: {
      size: NavSize.MEDIUM,
      justify: "around",
    },
  },
)

/**
 * Extract variant props type for NavList
 */
export type NavListVariantProps = VariantProps<typeof navListVariants>

/* ============================================
   Navigation Item Variants
   ============================================ */

/**
 * Navigation item styles
 * @description Styles for individual navigation buttons/links
 */
export const navItemVariants = cva(
  [
    // Flexbox centering
    "flex items-center justify-center",
    // Touch target minimum
    "min-w-[44px] min-h-[44px]",
    // Base padding
    "px-3 py-2",
    // Reset button styles
    "bg-transparent border-none cursor-pointer",
    "no-underline",
    // Typography
    "text-[0.625rem] leading-none font-medium",
    // Prevent text selection
    "select-none",
    // Remove tap highlight
    "[-webkit-tap-highlight-color:transparent]",
    // Touch action
    "touch-manipulation",
    // Transition
    "transition-colors duration-200 ease-out",
    // Focus styles
    "focus-visible:outline-2 focus-visible:outline-current focus-visible:outline-offset-2 focus-visible:rounded-lg",
    // Rounded for touch
    "rounded-xl",
  ],
  {
    variants: {
      /**
       * Current state of the item
       */
      state: {
        [NavItemState.INACTIVE]: ["text-muted-foreground", "hover:text-foreground", "hover:bg-accent/50"],
        [NavItemState.ACTIVE]: ["text-primary", "bg-primary/10"],
        [NavItemState.DISABLED]: ["text-muted-foreground/50", "cursor-not-allowed", "pointer-events-none"],
      },
      /**
       * Label position relative to icon
       */
      labelPosition: {
        [LabelPosition.BELOW]: "flex-col gap-1",
        [LabelPosition.BESIDE]: "flex-row gap-2",
        [LabelPosition.HIDDEN]: "",
      },
      /**
       * Size preset
       */
      size: {
        [NavSize.SMALL]: "px-2 py-1.5",
        [NavSize.MEDIUM]: "px-3 py-2",
        [NavSize.LARGE]: "px-4 py-3",
      },
    },
    defaultVariants: {
      state: NavItemState.INACTIVE,
      labelPosition: LabelPosition.BELOW,
      size: NavSize.MEDIUM,
    },
  },
)

/**
 * Extract variant props type for NavItem
 */
export type NavItemVariantProps = VariantProps<typeof navItemVariants>

/* ============================================
   Icon Container Variants
   ============================================ */

/**
 * Icon container styles
 * @description Styles for the icon wrapper
 */
export const iconVariants = cva(["flex items-center justify-center", "relative"], {
  variants: {
    /**
     * Icon size
     */
    size: {
      [IconSize.SMALL]: "w-[18px] h-[18px]",
      [IconSize.MEDIUM]: "w-6 h-6",
      [IconSize.LARGE]: "w-7 h-7",
    },
  },
  defaultVariants: {
    size: IconSize.MEDIUM,
  },
})

/**
 * Extract variant props type for Icon
 */
export type IconVariantProps = VariantProps<typeof iconVariants>

/* ============================================
   Label Variants
   ============================================ */

/**
 * Label text styles
 * @description Styles for the navigation item label
 */
export const labelVariants = cva(["font-medium", "whitespace-nowrap overflow-hidden text-ellipsis", "max-w-16"], {
  variants: {
    /**
     * Label visibility
     */
    position: {
      [LabelPosition.BELOW]: "text-[0.625rem]",
      [LabelPosition.BESIDE]: "text-xs",
      [LabelPosition.HIDDEN]: "sr-only",
    },
    /**
     * Size preset
     */
    size: {
      [NavSize.SMALL]: "text-[0.5625rem]",
      [NavSize.MEDIUM]: "text-[0.625rem]",
      [NavSize.LARGE]: "text-xs",
    },
  },
  defaultVariants: {
    position: LabelPosition.BELOW,
    size: NavSize.MEDIUM,
  },
})

/**
 * Extract variant props type for Label
 */
export type LabelVariantProps = VariantProps<typeof labelVariants>

/* ============================================
   Badge Variants
   ============================================ */

/**
 * Badge indicator styles
 * @description Styles for notification badges on nav items
 */
export const badgeVariants = cva(
  [
    "absolute",
    "flex items-center justify-center",
    "rounded-full",
    "font-bold",
    "bg-destructive text-destructive-foreground",
    "border-2 border-background",
  ],
  {
    variants: {
      /**
       * Badge type
       */
      type: {
        dot: "w-2.5 h-2.5 -top-0.5 -right-0.5",
        count: "min-w-[1.125rem] h-[1.125rem] px-1 -top-1 -right-1.5 text-[0.625rem]",
      },
    },
    defaultVariants: {
      type: "dot",
    },
  },
)

/**
 * Extract variant props type for Badge
 */
export type BadgeVariantProps = VariantProps<typeof badgeVariants>

/* ============================================
   Spacer Utility
   ============================================ */

/**
 * Spacer styles to prevent content from going under navigation
 * @description Used as a placeholder element below main content
 */
export const spacerVariants = cva(["w-full", "pb-[env(safe-area-inset-bottom,0px)]"], {
  variants: {
    /**
     * Size matching navigation height
     */
    size: {
      [NavSize.SMALL]: "h-14",
      [NavSize.MEDIUM]: "h-[4.5rem]",
      [NavSize.LARGE]: "h-22",
    },
  },
  defaultVariants: {
    size: NavSize.MEDIUM,
  },
})

/**
 * Extract variant props type for Spacer
 */
export type SpacerVariantProps = VariantProps<typeof spacerVariants>
